import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TextInputModern, Button } from '@pavanhiro/common-ui';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://go-auth-engine-dev-yaglve3mla-uc.a.run.app';

interface RegisterForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<RegisterForm>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Preserve OAuth2 flow params so a user can register and immediately continue
  // an in-progress authorization_code flow.
  const arToken    = searchParams.get('ar');
  const clientId   = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const state       = searchParams.get('state');

  const loginHref = arToken
    ? `/login?${searchParams.toString()}`
    : '/login';

  const validate = (): string | null => {
    if (!form.name.trim())   return 'Full name is required';
    if (!form.email)         return 'Email address is required';
    if (!/\S+@\S+\.\S+/.test(form.email)) return 'Please enter a valid email address';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');

    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:     form.name,
          email:    form.email,
          password: form.password,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        const msg = data?.error_description || data?.message || data?.error || 'Registration failed. Please try again.';
        throw new Error(msg);
      }

      setSuccess(true);

      // If registration also returns tokens and we are in an OAuth2 flow, redirect immediately.
      if (data?.access_token && arToken) {
        const completeResp = await fetch(`${API_BASE_URL}/oauth2/authorize/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ar: arToken, access_token: data.access_token }),
        });
        const completeData = await completeResp.json();
        if (completeResp.ok && completeData?.redirect_to) {
          window.location.href = completeData.redirect_to;
          return;
        }
      }

      // Otherwise, redirect to login after a short delay.
      setTimeout(() => {
        const params = new URLSearchParams();
        if (clientId)   params.set('client_id', clientId);
        if (redirectUri) params.set('redirect_uri', redirectUri);
        if (state)       params.set('state', state);
        const qs = params.toString();
        window.location.href = qs ? `/login?${qs}` : '/login';
      }, 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">ZoAuth</div>
        </div>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: 'var(--text-primary)' }}>
            Account created!
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6 }}>
            Your account has been created. Redirecting you to sign in…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">ZoAuth</div>
        <div className="auth-subtitle">Create your account</div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <TextInputModern
          name="name"
          label="Full name"
          variant="text"
          value={form.name}
          onChange={(value) => setForm({ ...form, name: value })}
          placeholder="Jane Smith"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          autoComplete="name"
        />

        <TextInputModern
          name="email"
          label="Work email"
          variant="email"
          value={form.email}
          onChange={(value) => setForm({ ...form, email: value })}
          placeholder="you@company.com"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          autoComplete="email"
        />

        <TextInputModern
          name="password"
          label="Password"
          variant="password"
          value={form.password}
          onChange={(value) => setForm({ ...form, password: value })}
          placeholder="Min. 8 characters"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          passwordToggle
          autoComplete="new-password"
        />

        <TextInputModern
          name="confirmPassword"
          label="Confirm password"
          variant="password"
          value={form.confirmPassword}
          onChange={(value) => setForm({ ...form, confirmPassword: value })}
          placeholder="Repeat password"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          passwordToggle
          autoComplete="new-password"
          error={error || undefined}
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={loading}
          loading={loading}
          style={{ width: '100%', marginTop: '8px' }}
        >
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <div className="footer-links" style={{ marginTop: '24px', textAlign: 'center' }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Already have an account?
        </span>{' '}
        <Link to={loginHref} className="footer-link">
          Sign in
        </Link>
      </div>

      <p style={{
        fontSize: '11px',
        color: 'var(--text-secondary)',
        textAlign: 'center',
        marginTop: '20px',
        lineHeight: 1.5,
      }}>
        By creating an account you agree to the{' '}
        <a href="https://zoauth.com/docs" style={{ color: 'var(--primary-color)' }}>
          Terms of Service
        </a>
        {' '}and{' '}
        <a href="https://zoauth.com/docs" style={{ color: 'var(--primary-color)' }}>
          Privacy Policy
        </a>.
      </p>
    </div>
  );
}
